import React, { useContext, useEffect, useState } from "react";
import { Box, Divider, List, ListItem } from "@mui/material";
import ActiveChats from "src/scenes/Chat/components/ActiveChats"; 
import UserAvatar from "src/scenes/Chat/components/UserAvatar";
import ListOfChats from "src/scenes/Chat/components/ListOfChats";
import SettingsIcon from "src/img/new/profile-settings.svg";
import SearchIcon from "src/img/new/search-gray.svg";
import { 
    Container, StyledInput,
    CompanyLogoContainer,
    Icon
} from "src/scenes/Chat/components/styled/chatListStyled";

import { UserContext } from "src/scenes/App/UserContext";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { UserKeys } from "src/constants/userDetails";
import { isUserOnline } from "src/utils/helpers";

import { SetPresence } from "matrix-js-sdk";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ManageLeaderShipRoleModal from "./components/modals/ManageLeaderShipRoleModal";
import styled from "styled-components";
import { TextInputWithIcon } from "src/components";
import ChatSkeletonTheme from "./components/ChatSkeletonTheme";

const ChatSkeletonAvatar = styled(Skeleton)`
    border: 1px solid transparent;
    border-radius: 0.625rem;
    cursor: pointer;
`;

const ChatList = (props) => {
    const { 
        useActiveChats: ctxUseActiveChats,
        useMatrixChatList: ctxUseMatrixChatList
    } = useContext(ChatContext);

    const {
        activeChatListDetails,
        isFetchingActiveChats,
        selectedChatListTypeOrLeadershipUuid,
        handleChatListSelectChange,
        leadershipRoles
    } = ctxUseActiveChats;
    const { 
        activeChatList
    } = ctxUseMatrixChatList;

    useEffect(() => {
        setUserChatsList(activeChatList);
    }, [activeChatList]);

    const [userChatsList, setUserChatsList] = useState(activeChatList ?? []);
    const [searchedChatList, setSearchedChatList] = useState(null);
    const [isChatListSearched, setIsChatListSearched] = useState(false);
    const [searchKey, setSearchKey] = useState("");

    const [leadershipModalVisible, setLeadershipModalVisible] = useState(false);
    const [leadershipModalData, setLeadershipModalData] = useState([]);

    const handleSearch = (e) => {
        const nextSearchKey = e.target.value;
        setSearchKey(nextSearchKey);
        if (nextSearchKey.length) {
            setIsChatListSearched(true);
            const _userChatsList = userChatsList ?? [];
            const filteredChats = _userChatsList.filter(chat =>
                chat.users.some(user => 
                    user.employeeDetails.firstName.toLowerCase().includes(nextSearchKey.toLowerCase())
                        || user.employeeDetails.lastName.toLowerCase().includes(nextSearchKey.toLowerCase())
                )    
            );
            setSearchedChatList(filteredChats);
        } else {
            setIsChatListSearched(false);
        }
    };
    
    const toggleLeadershipModal = (show, isCreate = true, data = []) => {
        setLeadershipModalVisible(show);
        setLeadershipModalData(data);
    };
    
    return (
        <ChatSkeletonTheme>
            <Container>
                <CompanyLogoElement />
            
                <Box sx={{ margin: "0.250rem 0.9375rem" }}>
                    <ActiveChatsElement
                        leadershipRoles={leadershipRoles}
                        selectedChatListTypeOrLeadershipUuid={selectedChatListTypeOrLeadershipUuid}
                        activeChatListDetails={activeChatListDetails}
                        isFetchingActiveChats={isFetchingActiveChats}
                        handleChatListSelectChange={handleChatListSelectChange}
                        toggleLeadershipModal={toggleLeadershipModal}
                    />

                    { !isFetchingActiveChats 
                        ? 
                        <TextInputWithIcon
                            iconSrc={SearchIcon}
                            iconPosition="right"
                            iconOffset="1rem"
                            iconWidth="24px"
                            iconHeight="24px"
                            maxWidth="21.5rem"
                            margin="1.4375rem 0 0.8rem 0"
                        >
                            <StyledInput
                                aria-label="Search chats"
                                autoComplete="off"
                                name="chat-search-filter"
                                placeholder="Search people"
                                spellCheck={false}
                                type="search"
                                value={searchKey}
                                onChange={handleSearch}
                            />
                        </TextInputWithIcon>
                        :
                        <Skeleton height={"2.5rem"} width={"100%"} style={{ margin: "1.4375rem 0 0.8rem 0" }} />
                    }
                </Box>
            
                <Divider />
                <ListOfChatsElement searchedChatList={isChatListSearched ? searchedChatList : userChatsList} />
                {leadershipModalVisible && (
                    <ManageLeaderShipRoleModal
                        showDialog={leadershipModalVisible}
                        handleClose={() => setLeadershipModalVisible(false)}
                        data={leadershipModalData}
                        props={props.props}
                    />
                )}

            </Container>
        </ChatSkeletonTheme>
    );
};

const CompanyLogoElement = () => {
    return (
        <CompanyLogoContainer>
            <UserAvatarElement />
        </CompanyLogoContainer>
    );
};

const UserAvatarElement = (props) => {
    const userContext = useContext(UserContext);
    const { 
        matrixClient,
        useChatMessage: ctxUseChatMessage,
        usePresenceListener: ctxUsePresenceListener,
        useBackupForm: ctxUseBackupForm
    } = useContext(ChatContext);

    const { 
        displayProfileModal,
    } = ctxUseChatMessage;

    const {
        handleSetOwnUserPresence
    } = ctxUsePresenceListener;

    const {
        handleShowBackupKeyModal
    } = ctxUseBackupForm;

    const onSettingsCogClicked = () => {
        handleShowBackupKeyModal(true);
    };

    const presenceOverride = userContext[UserKeys.CHAT_PRESENCE_OVERRIDE];
    const currentUserIsNewAndPresenceIsUnsetOrOverrideOnline = presenceOverride === "" ||
        presenceOverride === SetPresence.Online;
    const matrixUserId = matrixClient?.getUserId?.();
    const amIOnline = presenceOverride === SetPresence.Offline
        ? false
        : currentUserIsNewAndPresenceIsUnsetOrOverrideOnline ||
            Boolean(matrixClient && matrixUserId && isUserOnline(matrixClient, matrixUserId));
  
    return (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
            { userContext ? <UserAvatar 
                firstName={userContext[UserKeys.FIRST_NAME]} 
                borderPixel="2px"
                lastName={userContext[UserKeys.LAST_NAME]}
                photo={userContext[UserKeys.PROFILE_PICTURE_URL]}
                height="2.5rem" 
                width="2.5rem" 
                withShadow={true}
                onClickUserAvatarFunction={() => displayProfileModal(userContext, userContext.leadershipRoles, true)}
                isOnline={amIOnline}
                showPresenceStatus={true}
                handleSetOwnUserPresence={handleSetOwnUserPresence}
            /> : <Skeleton height="4rem" width="4rem" /> 
            }

            <div onClick={onSettingsCogClicked} style={{ width: "1.73rem", height: "1.73rem", textAlign: "right", cursor: "pointer" }}>
                <Icon width="1rem" height="1rem" color="#000"
                    src={SettingsIcon}
                />
            </div>
        </div>
    );
};

const ActiveChatsElement = ({ leadershipRoles, selectedChatListTypeOrLeadershipUuid, activeChatListDetails, isFetchingActiveChats, handleChatListSelectChange, toggleLeadershipModal }) => {
    const userContext = useContext(UserContext);

    const { 
        useCreateChatModal: ctxUseCreateChatModal,
        useChatListUnread: ctxUseChatListUnread
    } = useContext(ChatContext);
    const { 
        showCreateChatModalOrShowLeadershipEmployees
    } = ctxUseCreateChatModal;

    const {
        activeChatsTotalUnread
    } = ctxUseChatListUnread;
    
    return (
        <ActiveChats 
            currentUser={userContext}
            showCreateChatModalOrShowLeadershipEmployees={showCreateChatModalOrShowLeadershipEmployees}
            selectedChatListTypeOrLeadershipUuid={selectedChatListTypeOrLeadershipUuid}
            handleChatListSelectChange={handleChatListSelectChange}
            activeChatsTotalUnread={activeChatsTotalUnread}
            leadershipRoles={leadershipRoles}
            activeChatListDetails={activeChatListDetails}
            isFetchingActiveChats={isFetchingActiveChats}
            toggleLeadershipModal={toggleLeadershipModal}
        />
    );
};

const ListOfChatsElement = ({ searchedChatList }) => {
    const { 
        useActiveChats: ctxUseActiveChats
    } = useContext(ChatContext);

    const {
        isFetchingActiveChats,
        setLookingForLeadershipToChat
    } = ctxUseActiveChats;

    return (
        <>
            { !isFetchingActiveChats && searchedChatList
                ? <ListOfChats 
                    searchedChatList={searchedChatList}
                    setLookingForLeadershipToChat={setLookingForLeadershipToChat}
                /> 
                : <SkeletonListOfChats numberOfItems={10} />
            }
        </>
    );
};

const SkeletonListItem = () => {
    return (
        <>
            <ChatSkeletonAvatar
                height={50} 
                width={50} 
                variant="circular" 
                style={{ boxSizing: "border-box", padding: "0.625rem", borderRadius: "0.625rem", marginRight: "1rem" }} 
            />
            <div style={{ width: "100%" }}>
                <Skeleton height={24} width={"80%"} />
                <Skeleton height={18} width={"60%"} />
            </div>
        </>
    );
};

const SkeletonListOfChats = ({ numberOfItems }) => {
    return (
        <List>
            {Array.from({ length: numberOfItems }).map((_, index) => (
                <ListItem key={index} style={{ display: "flex" }}>
                    <SkeletonListItem />
                </ListItem>
            ))}
        </List>
    );
};
export default ChatList; 
